import { useState, useEffect } from "react";
import { Input, Button } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { tagJob, tagJobAll } from "../../store/actions";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTableHook, DynamicTableHelper } from "@workspace/common";
import DynamicTableWrapper from "./OuterDynamicTable/DynamicTableWrapper";
import {
  A_CANDIDATE_INITIAL_OPTIONS,
  A_CANDIDATE_MANDATORY_OPTIONS,
  R_CANDIDATE_INITIAL_OPTIONS,
  R_CANDIDATE_MANDATORY_OPTIONS,
} from "./CandidateMappingConstants";
import {
  JOB_STAGE_IDS,
  JOB_STAGE_STATUS,
} from "../../components/JobListing/JobListingConstants";
import {
  fetchCandidatesFields,
  fetchCandidateForm,
  resetCandidates,
  candidateRecommendationList,
  resetCandidateRecommendationList,
} from "../../../../candidate/src/store/actions";
import Switch from "@mui/material/Switch";
import TableItemDisplay from "@workspace/common/src/Components/DynamicTable/TableItemDisplay";
import TableRowsPerPageWithNav from "@workspace/common/src/Components/DynamicTable/TableRowsPerPageWithNav";
import { TooltipWrapper } from "@workspace/common";

function CandidateMappingOC({ jobId, offCanvasStatus, closeTag }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tableConfig, setTableConfig] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [checked, setChecked] = useState(true);
  const [combinedData, setCombinedData] = useState([]);

  // Get the candidate data from the store
  const candidatesData = useSelector(
    (state) => state.CandidateReducer.candidatesRecommendation
  );

  const dataOne = candidatesData?.candidates;
  const dataTwo = candidatesData?.candidatesMatched?.results;

  // Custom Render List
  const customRenderList = [
    {
      names: ["candidateSubmissionData.firstName"],
      render: (data, opt) => (
        <Link
          className="text-custom-primary text-decoration-underline link-primary"
          onClick={() => {
            const win = window.open(
              `/candidates/${data?.id}/snapshot`,
              "_blank"
            );
            win.focus();
          }}
        >
          <span>
            {data?.candidateSubmissionData?.firstName}{" "}
            {data?.candidateSubmissionData?.lastName}
          </span>
        </Link>
      ),
    },
  ];

  // Generate Candidate Config
  const generateCandidateConfig = () => {
    return [
      // Indexing Column
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      // Checkbox Column
      {
        header: (
          <div className="form-check">
            <Input
              className="form-check-input"
              type="checkbox"
              id="fodTable"
              checked={
                activeRow?.length > 0 &&
                activeRow?.length === candidatesData?.candidates?.length
              }
              onChange={(e) => selectAll(e?.target?.checked)}
            />
          </div>
        ),
        name: "checkbox",
        sort: false,
        sortValue: "checkbox",
        render: (data) => {
          return (
            <div className="form-check">
              <Input
                className="form-check-input"
                type="checkbox"
                name="chk_child"
                onChange={(e) => handleRowCheck(data?.id, e?.target?.checked)}
                checked={activeRow?.includes(data?.id)}
              />
            </div>
          );
        },
      },
      ...customConfig,
      // Match Percentage Column
      checked && {
        header: "% of Match",
        name: "matchPercentage",
        sort: true,
        render: (data, index) => (
          <div>
            <Link 
              className="text-custom-primary text-decoration-underline link-primary" 
              onClick={() => handleRowExpand(data)}
            >
              {roundUpToNearest5(data?.matchingResult?.total_score)}
            </Link>
          </div>
        ),
      },
      // Action Column
      {
        header: "Action",
        name: "action",
        sort: false,
        sortValue: "action",
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => (
          <div
            className="d-flex align-items-center justify-content-center cursor-pointer"
            onClick={() => handleTag(data)}
          >
            <span className="mdi mdi-check-circle-outline fs-4 text-true-primary"></span>
          </div>
        ),
      },
    ].filter((item) => item);
  };

  // Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    handleRowCheck,
    selectAllRows,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 10,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
      allActive: true,
    },
    checked ? R_CANDIDATE_MANDATORY_OPTIONS : A_CANDIDATE_MANDATORY_OPTIONS,
    checked ? R_CANDIDATE_INITIAL_OPTIONS : A_CANDIDATE_INITIAL_OPTIONS,
    customRenderList,
    generateCandidateConfig
  );

  // Get all the option groups
  useEffect(() => {
    dispatch(fetchCandidatesFields());
    dispatch(fetchCandidateForm("Candidate_basic_info"));
    return () => {
      dispatch(resetCandidates());
    };
  }, []);

  // Fetch recommended candidates when offCanvasStatus / checked changes
  useEffect(() => {
    const newAbortController = new AbortController();
    // Set the custom config data when searchFields is empty
    if (pageRequest?.searchFields?.length === 0) {
      checked
        ? setCustomConfigData(R_CANDIDATE_INITIAL_OPTIONS)
        : setCustomConfigData(A_CANDIDATE_INITIAL_OPTIONS);
    }
    // Only create a new abort controller when offCanvasStatus is true
    if (offCanvasStatus && pageRequest?.searchFields?.length > 0) {
      setAbortController(newAbortController);
      const jobPageRequest = { ...pageRequest, jobId: jobId };
      dispatch(
        candidateRecommendationList(
          DynamicTableHelper.cleanPageRequest(jobPageRequest),
          newAbortController.signal,
          checked ? "Recommendation" : "All"
        )
      );
    } else if (abortController) {
      abortController.abort();
      setAbortController(null);
      dispatch(resetCandidateRecommendationList());
    }
    return () => {
      // Abort the request when the component unmounts or if tagOffcanvas changes to false
      newAbortController.abort();
      dispatch(resetCandidateRecommendationList());
    };
  }, [pageRequest, offCanvasStatus, checked]);

  // Update the page info when candidate Data changes
  useEffect(() => {
    if (candidatesData) {
      if (checked) {
        const dataThree = dataOne?.map((item, index) => ({
          ...item,
          matchingResult: dataTwo[index],
        }));
        setCombinedData(dataThree);
      }
      setPageInfoData(candidatesData);
      setTableData(candidatesData?.candidates);
    }
  }, [candidatesData]);

  // Update the table config when customConfig changes
  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateCandidateConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateCandidateConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // Handle Row Expand
  const handleRowExpand = (data) => {
    setExpandedRow((prev) => (prev === data.id ? null : data.id));
  };

  //Round up value to nearest 5 multiple
  const roundUpToNearest5 = (value) => {
    return Math.ceil((value * 100) / 5) * 5;
  };

  // Handle Check Change
  const handleCheckChange = () => {
    setExpandedRow(null);
    setChecked(!checked);
  };

  // Select All Rows
  const selectAll = (isChecked) => {
    selectAllRows(isChecked);
    if (isChecked) {
      if (candidatesData?.candidates) {
        const ids = [];
        candidatesData?.candidates?.forEach((item) => {
          ids.push(item?.id);
        });
        setActiveRow(ids);
      }
    } else {
      setActiveRow([]);
    }
  };

  // Handle Tag Candidate
  const handleTag = (data) => {
    const candidateId = data?.id;
    const payload = {
      jobId: jobId,
      jobStageId: JOB_STAGE_IDS?.TAG,
      status: JOB_STAGE_STATUS?.COMPLETED,
      candidateId,
      stepName: "Profile",
      subStepName: "Tag",
    };
    try {
      dispatch(tagJob({ payload, navigate }));
      closeTag();
    } catch (error) {
      toast.error("Error tagging job.");
    }
  };

  // Handle Tag All Candidates
  const handleTagAll = () => {
    const payload = [];
    if (activeRow?.length > 0) {
      activeRow?.forEach((itemId) => {
        payload.push({
          jobId: jobId,
          jobStageId: JOB_STAGE_IDS?.TAG,
          status: JOB_STAGE_STATUS?.COMPLETED,
          candidateId: itemId,
          stepName: "Profile",
          subStepName: "Tag",
        });
      });
      dispatch(tagJobAll({ payload, navigate }));
      closeTag();
    } else {
      toast.error("Please select at least one checkbox.");
    }
  };

  return (
    <div>
      <div className="offcanvas-header pb-1">
        <div className="w-100 d-flex flex-row gap-3 align-items-center justify-content-between">
          <div className="d-flex flex-row align-items-center gap-3">
            <span className="fw-semibold fs-5">Candidates</span>
            <form
              className="search-box"
              onSubmit={pageRequestSet.setSearchTerm}
            >
              <Input
                type="text"
                placeholder="Search"
                className="form-control"
                value={search}
                style={{
                  width: "210px",
                  height: "40px",
                  borderColor: "#d3d3d3",
                }}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="mdi mdi-magnify search-icon"></span>
            </form>
          </div>

          <div className="d-flex flex-row align-items-center gap-2">
            <div className="d-flex flex-row align-items-center gap-2">
              {!checked && (
                <>
                  <TableItemDisplay pageInfo={pageInfo} />
                  <div
                    style={{
                      width: "2px",
                      height: "20px",
                      backgroundColor: "#adb5bd",
                      marginLeft: "12px",
                    }}
                  ></div>
                  <TableRowsPerPageWithNav
                    pageRequestSet={pageRequestSet}
                    pageInfo={pageInfo}
                    defaultValue={10}
                  />
                </>
              )}
              <div>
                <span>Recommendation</span>
                <Switch checked={checked} onChange={handleCheckChange} />
              </div>
              <TooltipWrapper tooltipText="Tag Candidates">
                <Button
                  className="btn btn-custom-primary"
                  onClick={() => handleTagAll()}
                  style={{
                    backgroundColor: "#0A65CC",
                    height: "40px",
                  }}
                >
                  <i className="ri-user-add-fill fs-5"></i>
                </Button>
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </div>
      <div className="offcanvas-body">
        <DynamicTableWrapper
          data={checked ? combinedData : candidatesData.candidates}
          config={tableConfig}
          setTableConfig={setTableConfig}
          pageInfo={pageInfo}
          pageRequest={pageRequest}
          pageRequestSet={pageRequestSet}
          jobId={jobId}
          expandedRow={expandedRow}
          closeTag={closeTag}
        />
      </div>
    </div>
  );
}

export default CandidateMappingOC;
